package com.bulut.mecitogullari.ui.screens.addtransaction

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.CashTransaction
import com.bulut.mecitogullari.data.model.Categories
import com.bulut.mecitogullari.data.model.Project
import com.bulut.mecitogullari.data.model.TransactionType
import com.bulut.mecitogullari.data.model.UnitPayment
import com.bulut.mecitogullari.data.model.UnitStatus
import com.bulut.mecitogullari.data.repository.ProjectRepository
import com.bulut.mecitogullari.data.repository.TransactionRepository
import com.bulut.mecitogullari.data.repository.UnitRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.math.roundToLong

/** Legacy single-entry sale mirror (pre-installments). */
private const val UNIT_SALE_PREFIX = "unit-sale-"

/** One received installment of a sold apartment. */
private const val UNIT_PAY_PREFIX = "unit-pay-"

data class AddTransactionUiState(
    val isEditing: Boolean = false,
    val type: TransactionType = TransactionType.EXPENSE,
    val dateEpochMillis: Long = todayUtcMidnightMillis(),
    val description: String = "",
    val amountText: String = "",
    val category: String = "",
    val projectId: String? = null,
    val unitId: String? = null,
    /** Off = record on the project only, keep it out of the cash ledger. */
    val inDailyLedger: Boolean = true,
    val projects: List<Project> = emptyList(),
    /** Sellable units of the selected project (plus the one being edited). */
    val availableUnits: List<ApartmentUnit> = emptyList(),
    val error: String? = null,
    val isSaving: Boolean = false,
    val closed: Boolean = false,
) {
    val categoryOptions: List<String>
        get() = if (type == TransactionType.INCOME) Categories.income else Categories.expense

    /** Apartment sale: project + unit become mandatory. */
    val isApartmentSale: Boolean
        get() = type == TransactionType.INCOME && category == Categories.APARTMENT_SALE
}

/**
 * Create & edit screen for a ledger entry.
 * [transactionId] is "" for a new entry, otherwise the entry being edited.
 *
 * Apartment sales are two-way: saving one marks the chosen unit SOLD
 * (price/date synced), and the entry shares the unit's stable ledger id so
 * the building view and this screen never produce duplicates.
 */
class AddTransactionViewModel(
    transactionId: String,
    private val transactionRepository: TransactionRepository,
    projectRepository: ProjectRepository,
    private val unitRepository: UnitRepository,
) : ViewModel() {

    private val editingId: String? = transactionId.ifBlank { null }
    private val editingPaymentId: String? =
        editingId?.takeIf { it.startsWith(UNIT_PAY_PREFIX) }?.removePrefix(UNIT_PAY_PREFIX)
    private var allUnits: List<ApartmentUnit> = emptyList()

    private val _uiState = MutableStateFlow(AddTransactionUiState(isEditing = editingId != null))
    val uiState: StateFlow<AddTransactionUiState> = _uiState.asStateFlow()

    init {
        editingId?.let { id ->
            viewModelScope.launch {
                transactionRepository.get(id)?.let { existing ->
                    _uiState.update {
                        it.copy(
                            type = existing.type,
                            dateEpochMillis = existing.dateEpochMillis,
                            description = existing.description,
                            amountText = existing.amount.roundToLong().toString(),
                            category = existing.category,
                            projectId = existing.projectId,
                            inDailyLedger = existing.inDailyLedger,
                            unitId = id.removePrefix(UNIT_SALE_PREFIX).takeIf { _ ->
                                id.startsWith(UNIT_SALE_PREFIX)
                            },
                        )
                    }
                    refreshAvailableUnits()
                }
            }
        }
        viewModelScope.launch {
            projectRepository.projects.collect { projects ->
                _uiState.update { it.copy(projects = projects) }
            }
        }
        viewModelScope.launch {
            unitRepository.units.collect { units ->
                allUnits = units
                // An installment entry carries no unit id; find its unit.
                if (editingPaymentId != null && _uiState.value.unitId == null) {
                    units.firstOrNull { unit -> unit.payments.any { it.id == editingPaymentId } }
                        ?.let { unit -> _uiState.update { it.copy(unitId = unit.id) } }
                }
                refreshAvailableUnits()
            }
        }
    }

    fun onTypeChange(type: TransactionType) =
        _uiState.update { it.copy(type = type, category = "", unitId = null, error = null) }

    fun onDateChange(epochMillis: Long) = _uiState.update { it.copy(dateEpochMillis = epochMillis) }

    fun onDescriptionChange(value: String) = _uiState.update { it.copy(description = value, error = null) }

    fun onAmountChange(value: String) = _uiState.update { it.copy(amountText = value, error = null) }

    fun onCategoryChange(value: String) {
        _uiState.update {
            it.copy(
                category = value,
                unitId = if (value == Categories.APARTMENT_SALE) it.unitId else null,
                error = null,
            )
        }
        refreshAvailableUnits()
    }

    fun onProjectChange(projectId: String?) {
        _uiState.update { it.copy(projectId = projectId, unitId = null, error = null) }
        refreshAvailableUnits()
    }

    fun onDailyLedgerChange(enabled: Boolean) =
        _uiState.update { it.copy(inDailyLedger = enabled, error = null) }

    fun onUnitChange(unitId: String?) = _uiState.update { it.copy(unitId = unitId, error = null) }

    private fun refreshAvailableUnits() {
        _uiState.update { state ->
            state.copy(
                availableUnits = allUnits
                    .filter { unit ->
                        unit.projectId == state.projectId &&
                            (unit.status != UnitStatus.SOLD || unit.id == state.unitId)
                    }
                    .sortedBy { it.number },
            )
        }
    }

    fun save() {
        val state = _uiState.value
        if (state.isSaving) return

        val amount = state.amountText.toLongOrNull()?.toDouble()
        when {
            state.description.isBlank() ->
                return _uiState.update { it.copy(error = Localization.strings.errorEnterDescription) }
            amount == null || amount <= 0.0 ->
                return _uiState.update { it.copy(error = Localization.strings.enterValidAmount) }
            state.category.isBlank() ->
                return _uiState.update {
                    it.copy(error = if (state.type == TransactionType.INCOME) Localization.strings.errorSelectIncomeType else Localization.strings.errorSelectExpenseCategory)
                }
            state.isApartmentSale && state.projectId == null ->
                return _uiState.update { it.copy(error = Localization.strings.errorSelectProjectForSale) }
            state.isApartmentSale && state.unitId == null ->
                return _uiState.update { it.copy(error = Localization.strings.errorSelectSoldUnit) }
            // Kept out of the ledger, it would otherwise be invisible everywhere.
            !state.inDailyLedger && state.projectId == null ->
                return _uiState.update {
                    it.copy(error = Localization.strings.errorProjectRequiredOffLedger)
                }
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isSaving = true, error = null) }
            val saleUnitId = if (state.isApartmentSale) state.unitId else null
            // A sale entered here is one received installment of the unit.
            val paymentId = if (saleUnitId != null && !(editingId?.startsWith(UNIT_SALE_PREFIX) ?: false)) {
                editingPaymentId ?: newId()
            } else {
                null
            }
            transactionRepository.upsert(
                CashTransaction(
                    id = editingId ?: paymentId?.let { "$UNIT_PAY_PREFIX$it" } ?: newId(),
                    type = state.type,
                    amount = amount!!,
                    description = state.description.trim(),
                    category = state.category,
                    projectId = state.projectId,
                    inDailyLedger = state.inDailyLedger,
                    dateEpochMillis = state.dateEpochMillis,
                )
            )
            // Mirror onto the apartment: mark sold, record the installment.
            saleUnitId?.let { unitId ->
                allUnits.firstOrNull { it.id == unitId }?.let { unit ->
                    val payments = if (paymentId != null) {
                        unit.payments.filterNot { it.id == paymentId } +
                            UnitPayment(paymentId, state.dateEpochMillis, amount)
                    } else {
                        unit.payments
                    }
                    unitRepository.updateUnit(
                        unit.copy(
                            status = UnitStatus.SOLD,
                            salePrice = unit.salePrice ?: amount,
                            saleDateEpochMillis = unit.saleDateEpochMillis ?: state.dateEpochMillis,
                            buyerName = unit.buyerName.ifBlank { state.description.trim() },
                            payments = payments,
                        )
                    )
                }
            }
            _uiState.update { it.copy(isSaving = false, closed = true) }
        }
    }

    fun delete() {
        val id = editingId ?: return
        viewModelScope.launch {
            transactionRepository.delete(id)
            when {
                // Deleting a legacy sale entry puts the unit back on the market.
                id.startsWith(UNIT_SALE_PREFIX) -> {
                    val unitId = id.removePrefix(UNIT_SALE_PREFIX)
                    allUnits.firstOrNull { it.id == unitId }?.let { unit ->
                        unitRepository.updateUnit(
                            unit.copy(status = UnitStatus.AVAILABLE, saleDateEpochMillis = null)
                        )
                    }
                }
                // Deleting an installment just removes it from the unit.
                editingPaymentId != null -> {
                    allUnits.firstOrNull { unit -> unit.payments.any { it.id == editingPaymentId } }
                        ?.let { unit ->
                            unitRepository.updateUnit(
                                unit.copy(payments = unit.payments.filterNot { it.id == editingPaymentId })
                            )
                        }
                }
            }
            _uiState.update { it.copy(closed = true) }
        }
    }

    /** Reset the one-shot close flag after navigation has been handled. */
    fun onClosedHandled() = _uiState.update { it.copy(closed = false) }
}
